import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '@/lib/api';

export interface AuthUser {
  email: string;
  name: string;
  picture?: string;
}

interface AuthStore {
  user: AuthUser | null;
  sessionToken: string | null;
  isAuthenticated: boolean;
  isVerifying: boolean;
  login: (user: AuthUser, sessionToken: string) => void;
  logout: () => Promise<void>;
  verifySession: () => Promise<boolean>;
}

const getInitialToken = () => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('teresita_session_token');
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      sessionToken: null,
      isAuthenticated: false,
      isVerifying: !!getInitialToken(),

      login: (user: AuthUser, sessionToken: string) => {
        if (typeof window !== 'undefined') {
          localStorage.setItem('teresita_session_token', sessionToken);
        }
        set({
          user,
          sessionToken,
          isAuthenticated: true,
          isVerifying: false,
        });
      },

      logout: async () => {
        try {
          await api.logout();
        } catch (err) {
          console.warn('Error en logout:', err);
        } finally {
          if (typeof window !== 'undefined') {
            localStorage.removeItem('teresita_session_token');
          }
          set({
            user: null,
            sessionToken: null,
            isAuthenticated: false,
            isVerifying: false,
          });
        }
      },

      verifySession: async () => {
        const state = get();
        // Si no hay sesión previa en caché, no está autenticado
        if (!state.sessionToken) {
          set({ isVerifying: false, isAuthenticated: false });
          return false;
        }

        try {
          const res = await api.getMe();
          if (res.authenticated && res.user) {
            set({
              user: res.user,
              isAuthenticated: true,
              isVerifying: false,
            });
            return true;
          } else {
            // Sesión expirada o revocada
            get().logout();
            return false;
          }
        } catch (err) {
          console.warn('Error verificando sesión, manteniendo estado en caché:', err);
          // Si falla por red (offline), mantenemos la autenticación en caché local
          set({ isVerifying: false });
          return state.isAuthenticated;
        }
      },
    }),
    {
      name: 'teresita_auth_session',
      partialize: (state) => ({
        user: state.user,
        sessionToken: state.sessionToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
